import { useEffect, useMemo } from "react";
import {
  BufferGeometry,
  Line as ThreeLine,
  LineBasicMaterial,
  Vector3 as ThreeVector3,
} from "three";
import { Earth } from "../../scene/Earth";
import { LabelSprite } from "../../scene/LabelSprite";
import { Sun } from "../../scene/Sun";
import { SimpleOrbitRig } from "../shared/SimpleOrbitRig";
import {
  SEASON_STATIONS,
  getEarthOrbitPosition,
  getEllipseCenter,
  type SeasonSnapshot,
} from "./geometry";

function OrbitRing() {
  const points = useMemo(
    () =>
      Array.from({ length: 129 }, (_, index) => {
        const day = (index / 128) * 365;
        return new ThreeVector3(...getEarthOrbitPosition(day));
      }),
    [],
  );
  const geometry = useMemo(
    () => new BufferGeometry().setFromPoints(points),
    [points],
  );
  const line = useMemo(
    () =>
      new ThreeLine(
        geometry,
        new LineBasicMaterial({
          color: "#70a9c7",
          opacity: 0.7,
          transparent: true,
        }),
      ),
    [geometry],
  );

  useEffect(
    () => () => {
      geometry.dispose();
      line.material.dispose();
    },
    [geometry, line.material],
  );

  return <primitive object={line} />;
}

export function SeasonsScene({
  classroomMode = false,
  isPlaying,
  showPerihelion,
  snapshot,
  speed,
}: {
  readonly classroomMode?: boolean;
  readonly isPlaying: boolean;
  readonly showPerihelion: boolean;
  readonly snapshot: SeasonSnapshot;
  readonly speed: 0.25 | 0.5 | 1 | 2 | 4 | 8;
}) {
  const sunlight = useMemo(
    () =>
      snapshot.earthToSun.map((component) => component * -12) as [
        number,
        number,
        number,
      ],
    [snapshot.earthToSun],
  );
  const orbitTarget = useMemo(() => getEllipseCenter(), []);

  return (
    <>
      <color attach="background" args={["#030812"]} />
      <hemisphereLight args={["#9ec5ff", "#1a140c", 0.22]} />
      <ambientLight color="#2a3548" intensity={0.08} />
      <directionalLight color="#fff8ef" intensity={3.1} position={sunlight} />
      <Sun classroomMode={classroomMode} position={[0, 0, 0]} radius={1.05} />
      <OrbitRing />
      {SEASON_STATIONS.map((station) => {
        const position = getEarthOrbitPosition(station.dayOfYear);
        const isActive = station.index === snapshot.seasonIndex;
        return (
          <group key={station.label} position={position}>
            {isActive ? null : (
              <mesh>
                <sphereGeometry args={[0.22, 16, 12]} />
                <meshBasicMaterial color="#73a9d8" opacity={0.45} transparent />
              </mesh>
            )}
            <LabelSprite
              color={isActive ? "#ffd166" : "#dce8f8"}
              position={[0, 0.7, 0]}
              text={station.shortName}
            />
          </group>
        );
      })}
      {showPerihelion ? (
        <>
          <group position={snapshot.ellipseCenter}>
            <mesh>
              <sphereGeometry args={[0.1, 10, 8]} />
              <meshBasicMaterial color="#8aa0b8" />
            </mesh>
            <LabelSprite
              color="#8aa0b8"
              position={[0, 0.4, 0]}
              text="Ellipse centre"
            />
          </group>
          <group position={snapshot.perihelionPosition}>
            <mesh>
              <sphereGeometry args={[0.16, 12, 10]} />
              <meshBasicMaterial color="#ffd166" />
            </mesh>
            <LabelSprite
              color="#ffd166"
              position={[0, 0.55, 0]}
              text="Perihelion · 3 Jan"
            />
          </group>
          <group position={snapshot.aphelionPosition}>
            <mesh>
              <sphereGeometry args={[0.16, 12, 10]} />
              <meshBasicMaterial color="#9cc3f0" />
            </mesh>
            <LabelSprite
              color="#9cc3f0"
              position={[0, 0.55, 0]}
              text="Aphelion · 4 Jul"
            />
          </group>
        </>
      ) : null}
      <group position={snapshot.earthPosition}>
        <Earth
          classroomMode={classroomMode}
          isPlaying={isPlaying}
          showRotation
          speed={speed}
        />
      </group>
      <SimpleOrbitRig maxDistance={32} minDistance={16} target={orbitTarget} />
    </>
  );
}
